import { useState } from "react"
import {
    Link,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { makeStyles } from "tss-react/mui"

import { useMoney } from "../../../../../currency/useMoney"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"

import {
    getPriceChangeDirection,
    isIncreaseRow,
    isNegativeRow,
    PreviewRow,
    PriceChangeDirection,
} from "./adjustPricesLib"

const useStyles = makeStyles()((theme) => ({
    hint: {
        color: theme.palette.tailwind.slate[500],
    },
    tableContainer: {
        overflowX: "auto",
    },
    table: {
        tableLayout: "fixed",
    },
    name: {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    group: {
        color: theme.palette.tailwind.slate[500],
    },
    current: {
        color: theme.palette.tailwind.slate[500],
    },
    next: {
        fontWeight: 700,
    },
    increase: {
        color: theme.palette.success.dark,
    },
    reduction: {
        color: theme.palette.error.dark,
    },
    unchanged: {
        color: theme.palette.tailwind.slate[400],
    },
    invalidRow: {
        background: theme.palette.tailwind.red[50],
    },
    numeric: {
        width: "8rem",
        textAlign: "right",
        whiteSpace: "nowrap",
    },
    footer: {
        paddingTop: ".75rem",
    },
}))

const PREVIEW_ROW_LIMIT = 6

type PreviewTableProps = Readonly<{
    rows: ReadonlyArray<PreviewRow>
}>

export function PreviewTable({ rows }: PreviewTableProps) {
    const { classes, cx } = useStyles()
    const moneyFactory = useMoney({})
    const [showAll, setShowAll] = useState(false)

    const visibleRows = showAll ? rows : rows.slice(0, PREVIEW_ROW_LIMIT)
    const hasMore = rows.length > visibleRows.length

    const directionClasses: Record<PriceChangeDirection, string> = {
        [PriceChangeDirection.INCREASE]: classes.increase,
        [PriceChangeDirection.REDUCTION]: classes.reduction,
        [PriceChangeDirection.UNCHANGED]: classes.unchanged,
    }
    const changeClassOf = (row: PreviewRow) => directionClasses[getPriceChangeDirection(row)]

    return (
        <TableContainer className={classes.tableContainer}>
            <Table size="small" className={classes.table}>
                <TableHead>
                    <TableRow>
                        <LocalizedStrict id="adjust-price-list-preview-column-item">
                            <TableCell>Item</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="adjust-price-list-preview-column-now">
                            <TableCell className={classes.numeric}>Now</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="adjust-price-list-preview-column-new">
                            <TableCell className={classes.numeric}>New price</TableCell>
                        </LocalizedStrict>
                        <LocalizedStrict id="adjust-price-list-preview-column-change">
                            <TableCell className={classes.numeric}>Change</TableCell>
                        </LocalizedStrict>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {visibleRows.map((row) => (
                        <TableRow key={row.productId} className={cx(isNegativeRow(row) && classes.invalidRow)}>
                            <TableCell>
                                <Typography variant="body2" className={classes.name} title={row.productName}>
                                    {row.productName}
                                </Typography>
                                <Typography variant="caption" className={cx(classes.group, classes.name)}>
                                    {row.productGroupName}
                                </Typography>
                            </TableCell>
                            <TableCell className={cx(classes.numeric, classes.current)}>
                                {moneyFactory.format(row.current)}
                            </TableCell>
                            <TableCell className={cx(classes.numeric, classes.next, changeClassOf(row))}>
                                {moneyFactory.format(row.next)}
                            </TableCell>
                            <TableCell className={cx(classes.numeric, changeClassOf(row))}>
                                {isIncreaseRow(row) && "+"}
                                {moneyFactory.format(row.difference)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <Stack direction="row" alignItems="center" spacing="1rem" className={classes.footer}>
                {hasMore ? (
                    <LocalizedStrict
                        id="adjust-price-list-preview-showing"
                        vars={{ shown: visibleRows.length, total: rows.length }}
                    >
                        <Typography variant="caption" className={classes.hint}>
                            Showing {visibleRows.length} of {rows.length} items.
                        </Typography>
                    </LocalizedStrict>
                ) : (
                    <LocalizedStrict id="adjust-price-list-preview-showing-all" vars={{ total: rows.length }}>
                        <Typography variant="caption" className={classes.hint}>
                            Showing all {rows.length} items.
                        </Typography>
                    </LocalizedStrict>
                )}
                {hasMore && (
                    <LocalizedStrict id="adjust-price-list-preview-show-all">
                        <Link component="button" type="button" variant="caption" onClick={() => setShowAll(true)}>
                            Show all
                        </Link>
                    </LocalizedStrict>
                )}
            </Stack>
        </TableContainer>
    )
}
